import React, { useState } from 'react';
import { useMilestones } from '../../context/MilestoneContext';
import { usePlayer } from '../../context/PlayerContext';
import { Target, PenTool, Award, Plus, X, Info } from 'lucide-react';
import StatCard from '../shared/StatCard';

const MilestoneCreator = () => {
  const { 
    createMilestone, 
    ACHIEVEMENT_TYPES, 
    MILESTONE_CATEGORIES, 
    MILESTONE_TYPES 
  } = useMilestones();
  
  const { playerData } = usePlayer();
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('season'); // 'season', 'game', 'career'
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState(MILESTONE_TYPES.BATTING);
  const [category, setCategory] = useState(MILESTONE_CATEGORIES.GAME);
  const [achievementType, setAchievementType] = useState(ACHIEVEMENT_TYPES.PERFORMANCE);
  const [criteria, setCriteria] = useState([{ stat: '', operator: '>=', value: '', unit: '' }]);
  const [thresholdValue, setThresholdValue] = useState('');
  
  // Function to handle selecting a default milestone template
  const handleSelectDefaultMilestone = async (templateId) => {
    setFormError(null);
    setFormSuccess(null);
    
    try {
      setLoading(true);
      
      // Define default milestone templates
      const defaultTemplates = {
        'default-batting-avg-300': {
          title: 'Batting .300',
          description: 'Achieve a batting average of .300 or higher for the season',
          type: MILESTONE_TYPES.BATTING,
          category: MILESTONE_CATEGORIES.SEASON,
          achievementType: ACHIEVEMENT_TYPES.PERFORMANCE,
          criteria: [{ stat: 'battingAvg', operator: '>=', value: '0.300', unit: '' }]
        },
        'default-hits-25': {
          title: '25 Hit Club',
          description: 'Record 25 hits in a single season',
          type: MILESTONE_TYPES.BATTING,
          category: MILESTONE_CATEGORIES.SEASON,
          achievementType: ACHIEVEMENT_TYPES.PERFORMANCE,
          criteria: [{ stat: 'hits', operator: '>=', value: '25', unit: '' }]
        },
        'default-rbi-15': {
          title: 'RBI Machine',
          description: 'Drive in 15 runs in a single season',
          type: MILESTONE_TYPES.BATTING,
          category: MILESTONE_CATEGORIES.SEASON,
          achievementType: ACHIEVEMENT_TYPES.PERFORMANCE,
          criteria: [{ stat: 'rbi', operator: '>=', value: '15', unit: '' }]
        },
        'default-game-3hits': {
          title: 'Perfect Day',
          description: 'Record 3 or more hits in a single game',
          type: MILESTONE_TYPES.BATTING,
          category: MILESTONE_CATEGORIES.GAME,
          achievementType: ACHIEVEMENT_TYPES.PERFORMANCE,
          criteria: [{ stat: 'hits', operator: '>=', value: '3', unit: 'perGame' }]
        },
        'default-pitching-win': {
          title: 'Winning Pitcher',
          description: 'Record 3 wins in a season',
          type: MILESTONE_TYPES.PITCHING,
          category: MILESTONE_CATEGORIES.SEASON,
          achievementType: ACHIEVEMENT_TYPES.PERFORMANCE,
          criteria: [{ stat: 'wins', operator: '>=', value: '3', unit: '' }]
        },
        'default-pitching-strikeouts': {
          title: 'Strikeout Artist',
          description: 'Record 15 strikeouts in a season',
          type: MILESTONE_TYPES.PITCHING,
          category: MILESTONE_CATEGORIES.SEASON,
          achievementType: ACHIEVEMENT_TYPES.PERFORMANCE,
          criteria: [{ stat: 'strikeouts', operator: '>=', value: '15', unit: '' }]
        },
        // Career milestones
        'default-career-hits-100': {
          title: 'Century Club',
          description: 'Record 100 career hits',
          type: MILESTONE_TYPES.BATTING,
          category: MILESTONE_CATEGORIES.CAREER,
          achievementType: ACHIEVEMENT_TYPES.CAREER,
          criteria: [{ stat: 'hits', operator: '>=', value: '100', unit: '' }]
        },
        'default-career-hr-10': {
          title: 'Power Hitter',
          description: 'Hit 10 career home runs',
          type: MILESTONE_TYPES.BATTING,
          category: MILESTONE_CATEGORIES.CAREER,
          achievementType: ACHIEVEMENT_TYPES.CAREER,
          criteria: [{ stat: 'homeRuns', operator: '>=', value: '10', unit: '' }]
        },
        'default-career-wins-10': {
          title: 'Ace Pitcher',
          description: 'Record 10 career pitching wins',
          type: MILESTONE_TYPES.PITCHING,
          category: MILESTONE_CATEGORIES.CAREER,
          achievementType: ACHIEVEMENT_TYPES.CAREER,
          criteria: [{ stat: 'wins', operator: '>=', value: '10', unit: '' }]
        }
      };
      
      // Get the selected template
      const template = defaultTemplates[templateId];
      
      if (!template) {
        throw new Error('Template not found');
      }
      
      // Create milestone from template
      await createMilestone({
        ...template,
        createdBy: playerData?.id,
        playerName: playerData?.name
      });
      
      // Show success message
      setFormSuccess(`${template.title} milestone created successfully!`);
      
      // Clear success message after a delay
      setTimeout(() => {
        setFormSuccess(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error creating milestone from template:', error);
      setFormError(error.message || 'Failed to create milestone');
    } finally {
      setLoading(false);
    }
  };
  
  // Available stats based on type
  const battingStats = [
    { value: 'hits', label: 'Hits' },
    { value: 'atBats', label: 'At Bats' },
    { value: 'singles', label: 'Singles' },
    { value: 'doubles', label: 'Doubles' },
    { value: 'triples', label: 'Triples' },
    { value: 'homeRuns', label: 'Home Runs' },
    { value: 'rbi', label: 'RBIs' },
    { value: 'runs', label: 'Runs' },
    { value: 'walks', label: 'Walks' },
    { value: 'strikeouts', label: 'Strikeouts' },
    { value: 'stolenBases', label: 'Stolen Bases' },
    { value: 'battingAvg', label: 'Batting Average' },
    { value: 'onBasePercentage', label: 'On-Base Percentage' },
    { value: 'sluggingPercentage', label: 'Slugging Percentage' },
    { value: 'ops', label: 'OPS' }
  ];
  
  const pitchingStats = [
    { value: 'inningsPitched', label: 'Innings Pitched' },
    { value: 'strikeouts', label: 'Strikeouts' },
    { value: 'walks', label: 'Walks' },
    { value: 'hits', label: 'Hits Allowed' },
    { value: 'earnedRuns', label: 'Earned Runs' },
    { value: 'totalRuns', label: 'Total Runs' },
    { value: 'wins', label: 'Wins' },
    { value: 'losses', label: 'Losses' },
    { value: 'saves', label: 'Saves' },
    { value: 'games', label: 'Games Pitched' },
    { value: 'completeGames', label: 'Complete Games' },
    { value: 'shutouts', label: 'Shutouts' },
    { value: 'era', label: 'ERA' },
    { value: 'whip', label: 'WHIP' }
  ];
  
  // Operators
  const operators = [
    { value: '>=', label: 'Greater than or equal to (≥)' },
    { value: '>', label: 'Greater than (>)' },
    { value: '<=', label: 'Less than or equal to (≤)' },
    { value: '<', label: 'Less than (<)' },
    { value: '==', label: 'Equal to (=)' }
  ];
  
  // Units for certain stats
  const getUnitOptions = (stat) => {
    if (['battingAvg', 'onBasePercentage', 'sluggingPercentage', 'ops', 'era', 'whip'].includes(stat)) {
      return [{ value: '', label: 'Value' }];
    }
    
    if (stat === 'inningsPitched') {
      return [
        { value: '', label: 'Innings' },
        { value: 'games', label: 'Games' }
      ];
    }
    
    return [
      { value: '', label: 'Count' },
      { value: 'perGame', label: 'Per Game' },
      { value: 'perSeason', label: 'Per Season' }
    ];
  };
  
  // Add criteria field
  const addCriteria = () => {
    setCriteria([...criteria, { stat: '', operator: '>=', value: '', unit: '' }]);
  };
  
  // Remove criteria field
  const removeCriteria = (index) => {
    const newCriteria = [...criteria];
    newCriteria.splice(index, 1);
    setCriteria(newCriteria);
  };
  
  // Update criteria field
  const updateCriteria = (index, field, value) => {
    const newCriteria = [...criteria];
    newCriteria[index][field] = value;
    setCriteria(newCriteria);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    
    // Validate form
    if (!title.trim()) {
      setFormError('Title is required');
      return;
    }
    
    if (!description.trim()) {
      setFormError('Description is required');
      return;
    }
    
    // Validate criteria
    const validCriteria = criteria.filter(c => c.stat && c.operator && c.value);
    if (validCriteria.length === 0) {
      setFormError('At least one valid criteria is required');
      return;
    }
    
    try {
      setLoading(true);
      
      // Create milestone
      await createMilestone({
        title,
        description,
        type,
        category,
        achievementType,
        criteria: validCriteria,
        thresholdValue: thresholdValue || null,
        createdBy: playerData?.id,
        playerName: playerData?.name
      });
      
      // Reset form
      setTitle('');
      setDescription('');
      setType(MILESTONE_TYPES.BATTING);
      setCategory(MILESTONE_CATEGORIES.GAME);
      setAchievementType(ACHIEVEMENT_TYPES.PERFORMANCE);
      setCriteria([{ stat: '', operator: '>=', value: '', unit: '' }]);
      setThresholdValue('');
      
      // Show success message
      setFormSuccess('Milestone created successfully!');
      
      // Hide form after a delay
      setTimeout(() => {
        setShowForm(false);
        setFormSuccess(null);
      }, 2000);
      
    } catch (error) {
      console.error('Error creating milestone:', error);
      setFormError(error.message || 'Failed to create milestone');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <StatCard className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium flex items-center">
          <Award size={18} className="text-yellow-500 mr-2" />
          Milestones & Achievements
        </h3>
        {!showForm && (
          <button 
            onClick={() => setShowForm(true)} 
            className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm flex items-center"
          >
            <Plus size={16} className="mr-1" />
            New Milestone
          </button>
        )}
      </div>
      
      {!showForm ? (
        <div>
          <div className="mb-6">
            <h4 className="text-md font-medium mb-3 flex items-center">
              <Award size={16} className="text-blue-500 mr-2" />
              Default Milestone Templates
            </h4>
            <p className="text-sm text-gray-600 dark:text-darkText-secondary mb-4">
              Choose from these pre-defined milestones based on a 25-game season, or create your own custom milestone below.
            </p>
            
            {formSuccess && (
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md text-sm text-green-700 dark:text-green-300 mb-4">
                {formSuccess}
              </div>
            )}
            
            {/* Tabs for different milestone types */}
            <div className="mb-4">
              <div className="flex border-b border-gray-200 dark:border-dark-400">
                <button
                  className={`py-2 px-4 font-medium text-sm ${
                    activeTab === 'season' 
                      ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400' 
                      : 'text-gray-500 dark:text-darkText-tertiary'
                  }`}
                  onClick={() => setActiveTab('season')}
                >
                  Season Milestones
                </button>
                <button
                  className={`py-2 px-4 font-medium text-sm ${
                    activeTab === 'game' 
                      ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400' 
                      : 'text-gray-500 dark:text-darkText-tertiary'
                  }`}
                  onClick={() => setActiveTab('game')}
                >
                  Game Milestones
                </button>
                <button
                  className={`py-2 px-4 font-medium text-sm ${
                    activeTab === 'career' 
                      ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400' 
                      : 'text-gray-500 dark:text-darkText-tertiary'
                  }`}
                  onClick={() => setActiveTab('career')}
                >
                  Career Milestones
                </button>
              </div>
            </div>
            
            {/* Season Milestones */}
            {activeTab === 'season' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Default Batting Milestones */}
                <div className="border border-gray-200 dark:border-dark-400 rounded-md p-3">
                  <h5 className="font-medium text-blue-600 dark:text-blue-400 mb-2 flex items-center">
                    <Target size={14} className="mr-1" />
                    Batting Milestones
                  </h5>
                  <div className="space-y-2">
                    <div 
                      className="p-2 hover:bg-gray-50 dark:hover:bg-dark-300 rounded-md cursor-pointer"
                      onClick={() => handleSelectDefaultMilestone('default-batting-avg-300')}
                    >
                      <div className="font-medium">Batting .300</div>
                      <div className="text-xs text-gray-500 dark:text-darkText-tertiary">
                        Achieve a batting average of .300 or higher for the season
                      </div>
                    </div>
                    <div 
                      className="p-2 hover:bg-gray-50 dark:hover:bg-dark-300 rounded-md cursor-pointer"
                      onClick={() => handleSelectDefaultMilestone('default-hits-25')}
                    >
                      <div className="font-medium">25 Hit Club</div>
                      <div className="text-xs text-gray-500 dark:text-darkText-tertiary">
                        Record 25 hits in a single season
                      </div>
                    </div>
                    <div 
                      className="p-2 hover:bg-gray-50 dark:hover:bg-dark-300 rounded-md cursor-pointer"
                      onClick={() => handleSelectDefaultMilestone('default-rbi-15')}
                    >
                      <div className="font-medium">RBI Machine</div>
                      <div className="text-xs text-gray-500 dark:text-darkText-tertiary">
                        Drive in 15 runs in a single season
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Default Pitching Milestones */}
                <div className="border border-gray-200 dark:border-dark-400 rounded-md p-3">
                  <h5 className="font-medium text-purple-600 dark:text-purple-400 mb-2 flex items-center">
                    <PenTool size={14} className="mr-1" />
                    Pitching Milestones
                  </h5>
                  <div className="space-y-2">
                    <div 
                      className="p-2 hover:bg-gray-50 dark:hover:bg-dark-300 rounded-md cursor-pointer"
                      onClick={() => handleSelectDefaultMilestone('default-pitching-win')}
                    >
                      <div className="font-medium">Winning Pitcher</div>
                      <div className="text-xs text-gray-500 dark:text-darkText-tertiary">
                        Record 3 wins in a season
                      </div>
                    </div>
                    <div 
                      className="p-2 hover:bg-gray-50 dark:hover:bg-dark-300 rounded-md cursor-pointer"
                      onClick={() => handleSelectDefaultMilestone('default-pitching-strikeouts')}
                    >
                      <div className="font-medium">Strikeout Artist</div>
                      <div className="text-xs text-gray-500 dark:text-darkText-tertiary">
                        Record 15 strikeouts in a season
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Game Milestones */}
            {activeTab === 'game' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Game Batting Milestones */}
                <div className="border border-gray-200 dark:border-dark-400 rounded-md p-3">
                  <h5 className="font-medium text-blue-600 dark:text-blue-400 mb-2 flex items-center">
                    <Target size={14} className="mr-1" />
                    Game Batting Milestones
                  </h5>
                  <div className="space-y-2">
                    <div 
                      className="p-2 hover:bg-gray-50 dark:hover:bg-dark-300 rounded-md cursor-pointer"
                      onClick={() => handleSelectDefaultMilestone('default-game-3hits')}
                    >
                      <div className="font-medium">Perfect Day</div>
                      <div className="text-xs text-gray-500 dark:text-darkText-tertiary">
                        Record 3 or more hits in a single game
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Career Milestones */}
            {activeTab === 'career' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Career Batting Milestones */}
                <div className="border border-gray-200 dark:border-dark-400 rounded-md p-3">
                  <h5 className="font-medium text-blue-600 dark:text-blue-400 mb-2 flex items-center">
                    <Target size={14} className="mr-1" />
                    Career Batting Milestones
                  </h5>
                  <div className="space-y-2">
                    <div 
                      className="p-2 hover:bg-gray-50 dark:hover:bg-dark-300 rounded-md cursor-pointer"
                      onClick={() => handleSelectDefaultMilestone('default-career-hits-100')}
                    >
                      <div className="font-medium">Century Club</div>
                      <div className="text-xs text-gray-500 dark:text-darkText-tertiary">
                        Record 100 career hits
                      </div>
                    </div>
                    <div 
                      className="p-2 hover:bg-gray-50 dark:hover:bg-dark-300 rounded-md cursor-pointer"
                      onClick={() => handleSelectDefaultMilestone('default-career-hr-10')}
                    >
                      <div className="font-medium">Power Hitter</div>
                      <div className="text-xs text-gray-500 dark:text-darkText-tertiary">
                        Hit 10 career home runs
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Career Pitching Milestones */}
                <div className="border border-gray-200 dark:border-dark-400 rounded-md p-3">
                  <h5 className="font-medium text-purple-600 dark:text-purple-400 mb-2 flex items-center">
                    <PenTool size={14} className="mr-1" />
                    Career Pitching Milestones
                  </h5>
                  <div className="space-y-2">
                    <div 
                      className="p-2 hover:bg-gray-50 dark:hover:bg-dark-300 rounded-md cursor-pointer"
                      onClick={() => handleSelectDefaultMilestone('default-career-wins-10')}
                    >
                      <div className="font-medium">Ace Pitcher</div>
                      <div className="text-xs text-gray-500 dark:text-darkText-tertiary">
                        Record 10 career pitching wins
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="text-center py-6 text-gray-500 dark:text-darkText-secondary border-t border-gray-200 dark:border-dark-400 mt-4 pt-4">
            <div className="mb-3">
              <Award size={48} className="mx-auto text-gray-300 dark:text-darkText-tertiary" />
            </div>
            <p className="mb-2">Create your own custom milestones to track your progress!</p>
            <p className="text-sm">Set goals for batting, pitching, and more.</p>
            <button 
              onClick={() => setShowForm(true)} 
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md text-sm flex items-center mx-auto"
            >
              <Plus size={16} className="mr-1" />
              Create Custom Milestone
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Info */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., '50 Hit Club' or 'Batting .300'"
                className="w-full p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm dark:bg-dark-400 dark:text-darkText-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this milestone represents"
                className="w-full p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm dark:bg-dark-400 dark:text-darkText-primary"
                rows={2}
              />
            </div>
          </div>
          
          {/* Type and Category */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => setType(MILESTONE_TYPES.BATTING)}
                  className={`flex-1 py-2 px-3 rounded-md text-sm flex items-center justify-center ${
                    type === MILESTONE_TYPES.BATTING
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-gray-100 text-gray-700 dark:bg-dark-400 dark:text-darkText-secondary'
                  }`}
                >
                  <Target size={16} className="mr-1.5" />
                  Batting
                </button>
                <button
                  type="button"
                  onClick={() => setType(MILESTONE_TYPES.PITCHING)}
                  className={`flex-1 py-2 px-3 rounded-md text-sm flex items-center justify-center ${
                    type === MILESTONE_TYPES.PITCHING
                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                      : 'bg-gray-100 text-gray-700 dark:bg-dark-400 dark:text-darkText-secondary'
                  }`}
                >
                  <PenTool size={16} className="mr-1.5" />
                  Pitching
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm dark:bg-dark-400 dark:text-darkText-primary"
              >
                <option value={MILESTONE_CATEGORIES.GAME}>Game</option>
                <option value={MILESTONE_CATEGORIES.SEASON}>Season</option>
                <option value={MILESTONE_CATEGORIES.CAREER}>Career</option>
                <option value={MILESTONE_CATEGORIES.SPECIAL}>Special</option>
              </select>
            </div>
          </div>
          
          {/* Achievement Type */}
          <div>
            <label className="block text-sm font-medium mb-1">Achievement Type</label>
            <select
              value={achievementType}
              onChange={(e) => setAchievementType(e.target.value)}
              className="w-full p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm dark:bg-dark-400 dark:text-darkText-primary"
            >
              <option value={ACHIEVEMENT_TYPES.PERFORMANCE}>Performance-based (Statistical thresholds)</option>
              <option value={ACHIEVEMENT_TYPES.STREAK}>Streak-based (Consecutive games)</option>
              <option value={ACHIEVEMENT_TYPES.COMPARATIVE}>Comparative (vs. personal best/league avg)</option>
              <option value={ACHIEVEMENT_TYPES.SEASONAL}>Seasonal (Full season achievements)</option>
              <option value={ACHIEVEMENT_TYPES.CAREER}>Career (Lifetime milestones)</option>
            </select>
          </div>
          
          {/* Criteria */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium">Criteria</label>
              <button
                type="button"
                onClick={addCriteria}
                className="text-blue-600 dark:text-darkText-link text-sm hover:underline flex items-center"
              >
                <Plus size={14} className="mr-1" />
                Add Condition
              </button>
            </div>
            
            <div className="space-y-3">
              {criteria.map((criterion, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <select
                    value={criterion.stat}
                    onChange={(e) => updateCriteria(index, 'stat', e.target.value)}
                    className="flex-1 p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm dark:bg-dark-400 dark:text-darkText-primary"
                  >
                    <option value="">Select Stat</option>
                    {(type === MILESTONE_TYPES.BATTING ? battingStats : pitchingStats).map((stat) => (
                      <option key={stat.value} value={stat.value}>
                        {stat.label}
                      </option>
                    ))}
                  </select>
                  
                  <select
                    value={criterion.operator}
                    onChange={(e) => updateCriteria(index, 'operator', e.target.value)}
                    className="w-24 p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm dark:bg-dark-400 dark:text-darkText-primary"
                  >
                    {operators.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.value}
                      </option>
                    ))}
                  </select>
                  
                  <input
                    type="text"
                    value={criterion.value}
                    onChange={(e) => updateCriteria(index, 'value', e.target.value)}
                    placeholder="Value"
                    className="w-20 p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm dark:bg-dark-400 dark:text-darkText-primary"
                  />
                  
                  {criterion.stat && (
                    <select
                      value={criterion.unit}
                      onChange={(e) => updateCriteria(index, 'unit', e.target.value)}
                      className="w-28 p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm dark:bg-dark-400 dark:text-darkText-primary"
                    >
                      {getUnitOptions(criterion.stat).map((unit) => (
                        <option key={unit.value} value={unit.value}>
                          {unit.label}
                        </option>
                      ))}
                    </select>
                  )}
                  
                  {criteria.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCriteria(index)}
                      className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            {/* Threshold for streak-based achievements */}
            {achievementType === ACHIEVEMENT_TYPES.STREAK && (
              <div className="mt-3">
                <label className="block text-sm font-medium mb-1">
                  Streak Threshold (consecutive games)
                </label>
                <input
                  type="number"
                  value={thresholdValue}
                  onChange={(e) => setThresholdValue(e.target.value)}
                  placeholder="e.g., 5 for a 5-game streak"
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 rounded-md text-sm dark:bg-dark-400 dark:text-darkText-primary"
                />
              </div>
            )}
          </div>
          
          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md text-sm text-blue-700 dark:text-blue-300 flex">
            <Info size={16} className="mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium mb-1">Tips for creating effective milestones:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-600 dark:text-blue-400">
                <li>Be specific with your criteria</li>
                <li>For batting averages, use decimal format (e.g., 0.300)</li>
                <li>For streak-based achievements, specify the consecutive game count</li>
                <li>Consider both short-term and long-term goals</li>
              </ul>
            </div>
          </div>
          
          {/* Error/Success Messages */}
          {formError && (
            <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md text-sm text-red-700 dark:text-red-300">
              {formError}
            </div>
          )}
          
          {formSuccess && (
            <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md text-sm text-green-700 dark:text-green-300">
              {formSuccess}
            </div>
          )}
          
          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 border border-gray-300 dark:border-dark-400 text-gray-700 dark:text-darkText-secondary rounded-md text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-md text-sm flex items-center"
            >
              {loading ? 'Creating...' : 'Create Milestone'}
            </button>
          </div>
        </form>
      )}
    </StatCard>
  );
};

export default MilestoneCreator;
